/**
 * Account menu — V3 §B1.
 *
 * The avatar was decorative: rendered with no click handler, so "log out" had
 * no route through the UI at all. This wraps it in the existing dropdown
 * primitive (Radix, so keyboard and focus management come for free).
 */

import { useNavigate } from 'react-router';
import { Keyboard, LogOut, Monitor, Moon, Settings, Sun, User } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { UserAvatar } from '@/components/ui/avatar';
import { useAuth } from '@/features/auth';
import { useUiStore, type Theme } from '@/stores/ui';

const THEMES: { value: Theme; label: string; Icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'system', label: 'System', Icon: Monitor },
];

export function AccountMenu() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const theme = useUiStore((s) => s.theme);
  const setTheme = useUiStore((s) => s.setTheme);
  const setShortcutSheetOpen = useUiStore((s) => s.setShortcutSheetOpen);

  if (!user) return null;

  const onLogout = async (): Promise<void> => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Account menu"
          data-testid="account-menu-trigger"
          className="rounded-full outline-none ring-offset-2 ring-offset-[var(--surface)] focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
        >
          <UserAvatar id={user._id} name={user.name} src={user.avatarUrl} size={26} />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="pb-1">
          <div className="truncate text-[13px] font-medium text-[var(--text)]">{user.name}</div>
          <div className="truncate text-[11px] font-normal vt-muted">{user.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={() => navigate('/settings/profile')}>
          <User className="size-3.5" />
          My profile
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/settings')}>
          <Settings className="size-3.5" />
          Settings
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => setShortcutSheetOpen(true)}>
          <Keyboard className="size-3.5" />
          Keyboard shortcuts
          <kbd className="vt-mono ml-auto rounded-[4px] border border-[var(--border)] px-1 text-[10px]">
            ?
          </kbd>
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wide vt-muted">
          Theme
        </DropdownMenuLabel>
        <div className="flex gap-1 px-1.5 pb-1.5" role="radiogroup" aria-label="Theme">
          {THEMES.map(({ value, label, Icon }) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={theme === value}
              onClick={() => setTheme(value)}
              className={`flex flex-1 flex-col items-center gap-1 rounded-[6px] border px-1 py-1.5 text-[11px] transition-colors duration-[120ms] ${
                theme === value
                  ? 'border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent)]'
                  : 'border-[var(--border)] vt-muted hover:bg-[var(--surface-2)]'
              }`}
            >
              <Icon className="size-3.5" />
              {label}
            </button>
          ))}
        </div>

        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void onLogout()} data-testid="logout">
          <LogOut className="size-3.5" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
